import { useState, lazy, Suspense, useEffect, useCallback, useMemo } from 'react';
import './onboarding-styles.css';
import { OnboardingHeader } from './components/onboarding-header';
import { OnboardingGreeting } from './components/onboarding-greeting';
import { OnboardingLoading } from './components/onboarding-loading';
import { OnboardingChatInput } from './components/onboarding-chat-input';
import { Snackbar } from '@/shared/components/Snackbar';
import type { DestinationSuggestion } from '@contracts';
import type { GlobeVisualization } from '../map/components/globe-map/GlobeVisualization';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  fetchOnboardingConfigThunk,
  // searchDestinationsThunk,
  // fetchRecommendationsThunk,
  setCurrentStep,
  selectOnboardingConfig,
  selectOnboardingConfigLoading,
  selectOnboardingConfigError,
  selectRecommendationsError,
  // selectDestinationSearchError,
  selectCurrentStep,
} from '@/features/onboarding/redux';
// import { selectIsLoggedIn } from '@/features/auth/redux';
import { useErrorToast } from './hooks';
// import { logger } from '@/shared/utils/logger';

// const DestinationInput = lazy(() =>
//   import('./components/destination-input').then((module) => ({
//     default: module.DestinationInput,
//   })),
// );

const TripPreference = lazy(() =>
  import('./components/trip-preference').then((module) => ({
    default: module.TripPreference,
  })),
);

const GlobeVisualizationLazy = lazy<typeof GlobeVisualization>(() =>
  import('../map/components/globe-map/GlobeVisualization').then((module) => ({
    default: module.GlobeVisualization,
  })),
);

export function Onboarding() {
  const dispatch = useAppDispatch();
  const config = useAppSelector(selectOnboardingConfig);
  const isConfigLoading = useAppSelector(selectOnboardingConfigLoading);
  const configError = useAppSelector(selectOnboardingConfigError);
  const recommendationsError = useAppSelector(selectRecommendationsError);
  const currentStep = useAppSelector(selectCurrentStep);
  // const isLoggedIn = useAppSelector(selectIsLoggedIn);

  // Use custom hook for error toast management
  const errorStates = useMemo(
    () => [configError, recommendationsError],
    [configError, recommendationsError],
  );
  const { snackbarProps } = useErrorToast({ errorStates, duration: 5000 });

  const [selectedDestinations, _setSelectedDestinations] = useState<
    readonly DestinationSuggestion[]
  >([]);
  // const [globeSelectedDestinationName, setGlobeSelectedDestinationName] = useState<
  //   string | undefined
  // >(undefined);
  const [isSubmitting, _setIsSubmitting] = useState(false);

  useEffect(() => {
    void dispatch(fetchOnboardingConfigThunk());
  }, [dispatch]);

  // const handleDestinationsChange = useCallback((destinations: readonly DestinationSuggestion[]) => {
  //   setSelectedDestinations(destinations);
  //   setGlobeSelectedDestinationName(undefined);
  // }, []);

  // const handleSubmit = useCallback(() => {
  //   if (selectedDestinations.length === 0) {
  //     return;
  //   }
  //   if (!isLoggedIn) {
  //     setShowLoginPrompt(true);
  //     return;
  //   }
  //   setIsSubmitting(true);
  //   const submitPreferences = async () => {
  //     try {
  //       const destinationIds = selectedDestinations.map((dest) => dest.id);
  //       await dispatch(fetchRecommendationsThunk({ destinationIds, locale: 'en-US' })).unwrap();
  //       dispatch(setCurrentStep(2));
  //     } catch (error) {
  //       logger.warn('Failed to submit preferences:', error);
  //     } finally {
  //       setIsSubmitting(false);
  //     }
  //   };
  //   void submitPreferences();
  // }, [selectedDestinations, isLoggedIn, dispatch]);

  // const handleSearch = useCallback(
  //   (query: string) => {
  //     void dispatch(searchDestinationsThunk(query));
  //   },
  //   [dispatch],
  // );

  // const handleGlobeExplore = useCallback((destinationName: string) => {
  //   setGlobeSelectedDestinationName(destinationName);
  // }, []);

  const handleStepChange = useCallback(
    (step: number) => {
      dispatch(setCurrentStep(step));
    },
    [dispatch],
  );

  if (isConfigLoading || config === null) {
    return (
      <div className="onboarding-container">
        <OnboardingHeader step={currentStep} />
        <OnboardingLoading />
      </div>
    );
  }

  return (
    <div className="onboarding-container">
      <OnboardingHeader step={currentStep} />
      <Suspense fallback={<OnboardingLoading />}>
        {currentStep === 1 && (
          <div className="onboarding-container-bg pt-[70px] 2xl:pt-[100px]">
            {/* <OnboardingGreeting heading={config.heading} /> */}
            <OnboardingGreeting />
            <div className="relative mx-auto flex items-center justify-center">
              {/* <GlobeVisualizationLazy onExploreDestination={handleGlobeExplore} /> */}
              <GlobeVisualizationLazy />
              <div className="absolute top-[300px] left-1/2 z-99999 flex min-h-[244px] w-[660px] -translate-x-1/2 flex-col gap-10 transition-all duration-300 xl:top-[400px] 2xl:top-[450px] [@media(max-height:670px)]:top-[350px]">
                {/* <DestinationInput
                  selectedDestinations={selectedDestinations}
                  onDestinationsChange={handleDestinationsChange}
                  onSearch={handleSearch}
                  onSubmit={handleSubmit}
                  destinationNameToSelect={globeSelectedDestinationName}
                  isSubmitting={isSubmitting}
                /> */}
                <OnboardingChatInput />
              </div>
            </div>
            <div className="pointer-events-none absolute bottom-0 left-0 z-9 h-[calc(100vh-500px)] w-full bg-gradient-to-b from-transparent via-white to-white" />
          </div>
        )}
        {currentStep === 2 && (
          <div className="trip-preference-container px-10">
            <TripPreference
              step={currentStep}
              handleStep={handleStepChange}
              destinations={selectedDestinations.map((dest) => dest.name)}
            />
          </div>
        )}
      </Suspense>
      {isSubmitting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <OnboardingLoading
            title="Submitting preferences..."
            subtitle="Please wait while we prepare your travel experience"
          />
        </div>
      )}
      <Snackbar {...snackbarProps} />
    </div>
  );
}
